import { z } from 'zod';
import { trackingSchema } from './tracking-schema';

/** The partner application posts here (React form and its plain twin). */
export const PARTNER_FORM_ENDPOINT = '/api/forms/partner';

/** The form's key through the delivery pipeline. */
export const PARTNER_FORM_KEY = 'partner';

/**
 * Who is applying, in the order the dropdown shows them. The label is the
 * stored value, so the notification reads without a lookup.
 */
export const PARTNER_TYPES = ['Hotel or resort', 'Event venue', 'Nonprofit or charity'] as const;
export type PartnerType = (typeof PARTNER_TYPES)[number];

/**
 * Control names shared by the partner form and its plain HTML twin. The
 * organization rides in `company` so the notification's header shows it like
 * any other form's company; the site is `siteUrl` because `website` is the
 * honeypot's legacy name (spam.ts).
 */
export const PARTNER_FIELDS = {
  firstName: 'firstName',
  lastName: 'lastName',
  email: 'email',
  phone: 'phone',
  company: 'company',
  partnerType: 'partnerType',
  siteUrl: 'siteUrl',
  partnerCity: 'partnerCity',
  details: 'details',
} as const;

/**
 * Venue, hotel and charity applications from /partners. Not leads: no Pardot
 * handler, no Ads conversion, and the notification says so. The free text is
 * `details`, not `message`, because the spam filter's phrase list scans
 * `message` for "backlinks" and "link exchange", which is what a partner
 * reading our linking terms may well write. The honeypot, CleanTalk and the
 * rate limit still apply.
 */
export const partnerSubmissionSchema = z.object({
  firstName: z.string().trim().min(1, 'First name is required').max(100),
  lastName: z.string().trim().min(1, 'Last name is required').max(100),
  email: z.string().trim().email('Enter a valid email address').max(200),
  phone: z.string().trim().max(40).optional().or(z.literal('')),
  company: z.string().trim().min(1, 'Add the name of your venue or organization').max(200),
  partnerType: z.enum(PARTNER_TYPES),
  siteUrl: z.string().trim().url('Enter a full URL, starting with https://').max(500),
  partnerCity: z.string().trim().min(2, 'Add the city you are in').max(120),
  details: z.string().trim().max(3_000).optional().or(z.literal('')),
  tracking: trackingSchema.optional(),
});

export type PartnerSubmission = z.infer<typeof partnerSubmissionSchema>;
